import { useRef, useState } from 'react';

import { Dialog, DialogContent } from '@mui/material';

import Button, { LoadableButtonProps } from '@components/Button';
import DialogCloseButton from '@components/DialogCloseButton';

import useOrganizationPermissions from '@hooks/useOrganizationPermissions';

import Experiment from '@models/Experiment';
import { SynapseFile } from '@models/integrations/synapse/SynapseFile';

import PlutoDialogTitle from '../../PlutoDialogTitle';
import SynapseFastqViewer from './SynapseFastqViewer';

type Props = {
    onSelected: (files: SynapseFile[]) => void;
    experiment: Experiment;
    onDoneUploading?: () => void;
} & LoadableButtonProps;
const SynapseModalButton = ({ onSelected, experiment, onDoneUploading, ...props }: Props) => {
    const { features } = useOrganizationPermissions();

    const [open, setOpen] = useState(false);
    const handleDone = (files: SynapseFile[]) => {
        onSelected(files);
        setOpen(false);
    };

    const handleDoneUploading = () => {
        if (onDoneUploading) {
            onDoneUploading();
        }
        setOpen(false);
    };

    // Reference to the SynapseFastqViewer component
    const viewerRef = useRef<React.ElementRef<typeof SynapseFastqViewer>>(null);

    // When the modal is closed
    const handleClose = () => {
        setOpen(false);
    };

    // If the feature is not enabled, don't render the button or modal
    if (!features?.experiment_features?.synapse_enabled) {
        return null;
    }

    return (
        <div>
            <Button
                onClick={() => {
                    setOpen(true);
                }}
                variant="outlined"
                color="primary"
                {...props}
            >
                Import from Synapse
            </Button>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
                <PlutoDialogTitle>Select files from Synapse</PlutoDialogTitle>
                <DialogCloseButton onClose={handleClose} />
                <DialogContent sx={{ height: '80vh', padding: '16px', display: 'flex', flexDirection: 'column' }}>
                    {open && (
                        <SynapseFastqViewer
                            ref={viewerRef}
                            modalView={true}
                            onFilesSelected={handleDone}
                            experiment={experiment}
                            onDoneUploading={handleDoneUploading}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SynapseModalButton;
